/**
 * Shared utility functions
 *
 * @package Felix
 */

/**
 * Debounce function
 *
 * @param {Function} func      Function to debounce
 * @param {number}   wait      Wait time in milliseconds
 * @param {boolean}  immediate Whether to call immediately
 * @return {Function} Debounced function
 */
export const debounce = (func, wait, immediate) => {
	let timeout
	return (...args) => {
		const later = () => {
			timeout = null
			if (!immediate) {
				func(...args)
			}
		}
		const callNow = immediate && !timeout
		clearTimeout(timeout)
		timeout = setTimeout(later, wait)
		if (callNow) {
			func(...args)
		}
	}
}

/**
 * Throttle function
 *
 * @param {Function} func Function to throttle
 * @param {number}   wait Wait time in milliseconds
 * @return {Function} Throttled function
 */
export const throttle = (func, wait) => {
	let inThrottle
	return function executedFunction(...args) {
		if (!inThrottle) {
			func.apply(this, args)
			inThrottle = true
			setTimeout(() => (inThrottle = false), wait)
		}
	}
}

/**
 * Breakpoints (matches CSS media queries)
 * Uses rem for consistency with CSS and respects user font-size preferences.
 *
 * sm: 40rem (640px)  - rare edge cases
 * md: 48rem (768px)  - tablet
 * lg: 64rem (1024px) - desktop (primary)
 * xl: 80rem (1280px) - wide
 */
export const BREAKPOINTS = {
	sm: '40rem',
	md: '48rem',
	lg: '64rem',
	xl: '80rem',
}

/**
 * Check if viewport matches a minimum breakpoint
 * Uses matchMedia with rem values (consistent with CSS media queries).
 *
 * @param {string} bp Breakpoint key ('sm', 'md', 'lg', 'xl') or custom value ('50rem')
 * @return {boolean} Whether viewport is at or above the breakpoint
 */
export const isBreakpoint = (bp) => {
	const value = BREAKPOINTS[bp] || bp
	return window.matchMedia(`(min-width: ${value})`).matches
}

/**
 * Check if viewport is below the lg breakpoint (< 1024px)
 *
 * @return {boolean} Whether viewport is mobile/tablet
 */
export const isMobile = () => !isBreakpoint('lg')

/**
 * Check if viewport is at or above the lg breakpoint (>= 1024px)
 *
 * @return {boolean} Whether viewport is desktop
 */
export const isDesktop = () => isBreakpoint('lg')

/**
 * Check if element is in viewport
 *
 * @param {HTMLElement} element Element to check
 * @return {boolean} Whether element is in viewport
 */
export const isInViewport = (element) => {
	const rect = element.getBoundingClientRect()
	const { innerHeight, innerWidth } = window
	const { clientHeight, clientWidth } = document.documentElement

	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (innerHeight || clientHeight) &&
		rect.right <= (innerWidth || clientWidth)
	)
}
